import React, { useState, useRef } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import { container } from '@infrastructure/container';


const BolCallback: React.FC = (props: any) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const hasConnected = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [state, setState] = useState<string | null>(null);

    useEffect(() => {
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        const callbackUrl = import.meta.env.VITE_BOL_CALLBACK_URL;
        
        setState(state);

        if (code && !hasConnected.current) {
            hasConnected.current = true;

            // The connection flow carries a `reconnect=<connectionId>` query in the OAuth state
            // when re-authorizing an existing connection. Pull it out and compute where to navigate.
            let reconnectConnectionId: string | null = null;
            let navTarget = '/';
            if (state) {
                try {
                    const decodedState = decodeURIComponent(state);
                    const [path, queryString = ''] = decodedState.split('?');
                    const params = new URLSearchParams(queryString);
                    reconnectConnectionId = params.get('reconnect');
                    params.delete('reconnect');
                    const remaining = params.toString();
                    navTarget = remaining ? `${path}?${remaining}` : path;
                } catch (err) {
                    navTarget = state;
                }
            }

            const request = reconnectConnectionId
                ? container.bolConnectorRepository.reconnect(reconnectConnectionId, code, callbackUrl)
                : container.bolConnectorRepository.addBolMarketplaceConnection(code, callbackUrl);

            request.then((response) => {
                if (response.status === 'error') {
                    setError(response.error.message ?? 'Failed to connect to Bol.com');
                    return;
                }
                navigate(navTarget);
            }).catch((error) => {
                setError(error.message);
            }).finally(() => {
                setIsLoading(false);
            });
        } else if (!code) {
            error ? setError(errorDescription) : setError('Something went wrong');
            setIsLoading(false);
        }
    }, [searchParams, navigate]);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 0
        }}>
            <DialogContainer maxWidth="600px" >
                <Box sx={{ position: 'relative', margin: 4 }}>

                    <Box sx={{ textAlign: 'left', position: 'relative' }}>
                        <img
                            src="/assets/images/ic_just_stock.png"
                            alt="JustStock Logo"
                            style={{ height: 16 }}
                        />
                    </Box>

                    {isLoading && (
                        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Box sx={{ pt: 3 }}>

                            <Box sx={{
                                p: 2,
                                bgcolor: 'error.light',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'error.main'
                            }}>
                                <Typography color="error.main">{error}</Typography>
                            </Box>

                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Button variant="outlined" onClick={() => {
                                    if (state) {
                                        try {
                                            const decodedState = decodeURIComponent(state);
                                            navigate(decodedState);
                                        } catch (error) {
                                            navigate(state);
                                        }
                                    } else {
                                        navigate('/');
                                    }
                                }}>
                                    Back to home
                                </Button>
                            </Stack>
                        </Box>
                    )}

                </Box>
            </DialogContainer>
        </Box>
    );
};

export default BolCallback;
